import { apiService } from './api';
import type { AdmissionApplication } from '../types/admissions';

/**
 * Student 360 — the operator-friendly "type a GR number, see everything"
 * endpoint. See backend/src/controllers/studentProfileController.ts for the
 * response shape; typed loosely here because the FE renders the response
 * tab-by-tab (each tab projects what it needs).
 */

export interface StudentProfileResponse {
  person: {
    _id: string;
    /** Given at enrollment — an applicant has none yet. */
    grNumber?: string;
    name: string;
    nameUrdu?: string;
    gender: 'male' | 'female' | 'other';
    dob?: string | null;
    cnic: string;
    phone?: string;
    email?: string;
    kind: 'applicant' | 'student' | 'alumni';
    photoFilename?: string;
  };
  family: {
    _id: string;
    displayName?: string;
    status: 'candidate' | 'resolved';
  } | null;
  guardians: Array<{
    _id: string;
    name: string;
    phone: string;
    relation: string;
    cnic: string;
    cnicIsPlaceholder: boolean;
  }>;
  siblings: Array<{ _id: string; grNumber?: string; name: string; kind: string; photoFilename?: string }>;
  student: {
    _id: string;
    grNumber: string;
    batchId?: { _id: string; name: string } | string;
    rollNumber?: string;
    isActive: boolean;
  } | null;
  enrollments: any[];
  admissions: AdmissionApplication[];
  fees: {
    invoices: any[];
    payments: any[];
    concessions: any[];
    /** Posted fee refunds and returned deposits: money given back. */
    refunds: ProfileRefund[];
  };
  letters: any[];
}

export interface ProfileRefund {
  _id: string;
  kind: 'REFUND' | 'DEPOSIT_REFUND';
  invoiceId: string;
  amount: number;
  method: string | null;
  reference: string | null;
  decidedAt: string;
}

export const studentProfileService = {
  /** `ref` is a GR number, or the Person id of an applicant (no GR number yet). */
  get: async (ref: string): Promise<StudentProfileResponse> => {
    const r = await apiService.get<StudentProfileResponse>(`/student-profile/${ref}`);
    return r.data!;
  },
};
